import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { parseFeatureResponse, resolveCapabilities, type FeatureStatus } from '../features/capabilities';

interface FeatureState {
  features: Record<string, boolean>;
  /** True only after a successful discovery, never after a transport/validation error. */
  loaded: boolean;
  status: FeatureStatus;
  loadFeatures: () => Promise<void>;
}

// Deployment-scoped, not cleared by zone switches. Deduplicate concurrent startup/retry calls.
let inFlight: Promise<void> | null = null;
export const useFeatureStore = create<FeatureState>()(
  devtools(set => ({
    features: {},
    loaded: false,
    status: 'idle',
    loadFeatures: () => {
      if (inFlight) return inFlight;
      set({ features: {}, loaded: false, status: 'loading' }, false, 'loadFeatures/start');
      inFlight = (async () => {
        try {
          const response = await fetch('/api/config/features', { signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error(`Feature discovery: HTTP ${response.status}`);
          const features = parseFeatureResponse(await response.json());
          set({ features, loaded: true, status: 'ready' }, false, 'loadFeatures/success');
        } catch (error) {
          console.warn('[featureStore] Feature-Discovery fehlgeschlagen:', error);
          set({ features: {}, loaded: false, status: 'error' }, false, 'loadFeatures/error');
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    },
  }))
);

export const useCapabilities = () => useFeatureStore(useShallow(resolveCapabilities));
export const useNetzanschlussEnabled = () => useFeatureStore(s => resolveCapabilities(s).connectionCheck);
export const useInternalMetricsEnabled = () => useFeatureStore(s => resolveCapabilities(s).diagnostics);
// Unknown configuration must not expose editing controls while discovery is in flight/failed.
export const useReadOnlyUi = () => useFeatureStore(s => !resolveCapabilities(s).edit);
export const useStresstestEnabled = () => useFeatureStore(s => resolveCapabilities(s).planning);
export const useSchalthistorieEnabled = () => useFeatureStore(s => resolveCapabilities(s).switchingHistory);
export const usePrognoseEnabled = () => useFeatureStore(s => resolveCapabilities(s).forecast);
export const usePrognoseKnownOff = () => useFeatureStore(s => s.status === 'ready' && s.features.prognose === false);
export const useSteuerungEnabled = () => useFeatureStore(s => resolveCapabilities(s).control);
export const useControlDemoEnabled = () => useFeatureStore(s => resolveCapabilities(s).controlDemo);
